import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import {
  HeroStoryProvider,
  HeroImages,
  HeroHeadline,
  HeroProgress,
} from "@/components/HeroStory";

const A = "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/";

const IMG = {
  backstage: { src: `${A}uj9fl5on_apt%20dude.jpg`, alt: "Man in a velvet blazer backstage pressing the glowing blue face of his WearSpray band", tf: "scale(1.08)", pos: "56% 22%", posM: "56% 30%" },
  jogger: { src: `${A}yuqdhv1s_jogger%202.jpg`, alt: "Woman running along the coast at sunset wearing a WearSpray band", tf: "scale(1.12)", pos: "56% 22%", posM: "47% 32%" },
  kayaker: { src: `${A}040ece63_kayaker%202.jpg`, alt: "Man kayaking on an alpine lake wearing a WearSpray band", tf: "scale(1.02)", pos: "52% 10%", posM: "50% 22%" },
  surfer: { src: `${A}lvz203ks_surfer%20-%20blue.jpg`, alt: "Surfer riding a wave at sunset wearing a WearSpray band", tf: "scale(1.02)", pos: "52% 8%", posM: "52% 22%" },
  surfer2: { src: `${A}r793jqly_surfer%20girl%204.jpg`, alt: "Surfer girl riding inside a wave, WearSpray band on her wrist", tf: "scale(1.04)", pos: "50% 22%", posM: "52% 28%" },
  airport: { src: `${A}2ey5nxlg_airport%20lounge.jpg`, alt: "Woman in an airport lounge at night adjusting her WearSpray band with a plane beyond the window", tf: "scale(1.08)", pos: "44% 20%", posM: "44% 28%" },
  burgundy: { src: `${A}w0cdcvjn_elegant%20gallery.jpg`, alt: "Woman in a burgundy gown on a rooftop terrace at night wearing a WearSpray band", tf: "scale(1.08)", pos: "54% 20%", posM: "48% 28%" },
};

const H1 = "block text-2xl sm:text-4xl md:text-5xl leading-[1.08]";
const H2 = "block text-3xl sm:text-5xl md:text-6xl leading-[1.04]";

const QUESTIONS = [
  <>Searching for the <span className="text-accent">sunscreen?</span></>,
  <><span className="text-accent">Fragrance</span> or body mist somewhere else?</>,
];
function QuestionStack() {
  return (
    <div data-testid="products-question-stack">
      <span className="block font-heading text-lg sm:text-xl md:text-2xl font-light text-zinc-300 mb-3">
        Has this happened to you?
      </span>
      <div className="space-y-1 md:space-y-1.5">
        {QUESTIONS.map((q, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.4, delay: i * 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="block font-heading text-xl sm:text-3xl md:text-4xl font-light leading-[1.14] text-white"
          >
            {q}
          </motion.span>
        ))}
      </div>
    </div>
  );
}

const STEPS = [
  { dur: 5600, bg: { type: "image", ...IMG.burgundy }, headline: <QuestionStack /> },
  {
    dur: 5200,
    bg: { type: "image", ...IMG.backstage },
    headline: (
      <div className="max-w-[560px]">
        <span className={H1}>WearSpray puts personal care right where you&nbsp;need&nbsp;it.</span>
        <span className={`${H2} text-accent mt-2`}>Right within reach.</span>
      </div>
    ),
  },
  {
    dur: 4400,
    bg: { type: "image", ...IMG.jogger },
    headline: (
      <>
        <span className={H1}>No searching. No bottle. No pause.</span>
        <span className={`${H1} text-accent mt-2`}>Just press, apply, and keep moving.</span>
      </>
    ),
  },
  {
    dur: 3800,
    bg: { type: "image", ...IMG.kayaker },
    headline: (
      <span className={H2}>
        Prepared. Protected. <span className="text-accent">Confident.</span>
      </span>
    ),
  },
  {
    dur: null,
    bg: { type: "image", ...IMG.airport },
    headline: (
      <>
        <span className={H1}>The New</span>
        <span className={`${H1} text-accent`}>Point of Access.</span>
      </>
    ),
  },
];
const LOOP = [IMG.surfer, IMG.burgundy, IMG.backstage, IMG.surfer2, IMG.jogger, IMG.airport, IMG.kayaker];

export default function ProductHero() {
  return (
    <section
      className="relative h-[82vh] min-h-[540px] w-full overflow-hidden bg-black"
      data-testid="product-hero-banner"
    >
      <HeroStoryProvider steps={STEPS} loopImages={LOOP}>
        <HeroImages />

        {/* Light bottom scrim, just enough for the bottom copy to stay legible */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

        {/* Overlaid copy */}
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 md:px-12 w-full pb-16 md:pb-24">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-mono text-xs tracking-[0.25em] uppercase text-accent mb-5"
              data-testid="products-hero"
            >
              The Product
            </motion.p>

            <HeroHeadline
              className="max-w-4xl h-[200px] sm:h-[230px] md:h-[250px] text-white"
            />

            <HeroProgress className="mt-8 max-w-sm" />
          </div>
        </div>

        {/* Scroll cue */}
        <div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 pointer-events-none"
          data-testid="products-scroll-cue"
        >
          <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-300">
            Scroll to explore
          </span>
          <ChevronDown size={18} className="text-accent animate-scroll-cue" />
        </div>
      </HeroStoryProvider>
    </section>
  );
}
